import Link from "next/link";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { SubmitHandler } from "react-hook-form";
import { useRouter } from "next/router";

import { Button, Card, Input } from "@chef/components";
import { BRAND_PATHS, NEW_PASSWORD_MIN_LENGTH } from "@chef/constants";
import { useExtendedForm } from "@chef/hooks";
import {
  useUpdatePasswordMutation,
  useUserInfoQuery,
} from "@chef/state-management";

import { MyAccountLayout } from "../../components/MyAccountLayout";

import { intl } from "./MyAccountChangePasswordPage.Intl";
interface IFormInput {
  newPassword: string;
  confirmPassword: string;
}

const schema = yup
  .object({
    newPassword: yup
      .string()
      .min(NEW_PASSWORD_MIN_LENGTH, intl.PASSWORD_MUST_BE_MIN_CHARACTERS)
      .required(),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref("newPassword"), null], intl.PASSWORDS_MUST_MATCH),
  })
  .required();

export const MyAccountChangePasswordPage = () => {
  const router = useRouter();

  const [updatePasswordMutation, { isLoading: isUpdatingPassword }] =
    useUpdatePasswordMutation();
  const { data: userInfoQuery } = useUserInfoQuery();
  const email = userInfoQuery?.userInfo.email;

  const { register, handleSubmit } = useExtendedForm<IFormInput>({
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
    resolver: yupResolver(schema),
    mode: "all",
  });

  const onSubmit: SubmitHandler<IFormInput> = async ({ newPassword }) => {
    try {
      await updatePasswordMutation({
        newPassword: newPassword,
        oldPassword: "",
      }).unwrap();

      router.push(BRAND_PATHS.PERSONAL_INFORMATION_PAGE);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <MyAccountLayout page="accountInformation" title={intl.PASSWORD}>
      <section className="col-span-5 space-y-6">
        <Card id="min-konto-change-password-grid">
          <form
            className="flex flex-col gap-8"
            onSubmit={handleSubmit(onSubmit)}
          >
            {/* This hidden input field is here for accessability for password managers */}
            <Input
              className="hidden"
              name="username"
              autoComplete="username"
              readOnly
              disabled
              value={email || ""}
              label="email"
            />
            <Input
              {...register("newPassword")}
              label={intl.NEW_PASSWORD}
              autoComplete="off"
              type="password"
              helperText={intl.PASSWORD_MUST_BE_MIN_CHARACTERS}
            />
            <Input
              {...register("confirmPassword")}
              label={intl.CONFIRM_NEW_PASSWORD}
              autoComplete="off"
              type="password"
            />

            <div className="flex flex-col gap-4 lg:flex-row-reverse lg:self-end">
              <Button
                primary
                loading={isUpdatingPassword}
                id="submit-update-password-button"
                onSubmit={handleSubmit(onSubmit)}
              >
                {intl.SUBMIT_BUTTON_TEXT}
              </Button>

              <Link
                href={BRAND_PATHS.PERSONAL_INFORMATION_PAGE}
                passHref
                legacyBehavior
              >
                <Button
                  disabled={isUpdatingPassword}
                  outlined
                  id="cancel-update-password-button"
                >
                  {intl.CANCEL_BUTTON_TEXT}
                </Button>
              </Link>
            </div>
          </form>
        </Card>
      </section>
    </MyAccountLayout>
  );
};
